import { t } from "i18next";
import { isNil, isObject } from "lodash";

import { FULL_LOAD_BULK_SET_BOOKINGS_PERCENT } from "../../../constants";
import { BookingsPollingStatusEnum } from "../../../enums";
import { Log } from "../../../helpers";
import {
  defaultBookingBulkCreateState,
  IBookingPollingError,
  IBookingsPollingCombinedState,
  IBookingsPollingUiState,
} from "../../../models";

const isBookingBulkCreateStateError = (
  errorData: unknown,
): errorData is IBookingPollingError => {
  return (
    isObject(errorData) &&
    "error" in errorData &&
    "passengerId" in errorData &&
    "date" in errorData
  );
};

const resolveBulkBookingError = (
  errorData: unknown,
): IBookingPollingError | null => {
  if (isNil(errorData)) {
    return null;
  }

  if (!isBookingBulkCreateStateError(errorData)) {
    Log.error(
      t("errors.receivedBulkBookingErrorWrongTypeError", {
        errorData: errorData,
      }),
    );
    return null;
  }

  return errorData as IBookingPollingError;
};

export const resolvePollingState = (
  pollingState: IBookingsPollingCombinedState,
): IBookingsPollingUiState => {
  const {
    isPreview = true,
    current,
    totalCount,
    newBookingsIds = [],
    conflictErrors,
    translatableErrors,
    errors,
    isAborted = false,
    status,
  } = pollingState;

  const baseState = {
    isPreview,
    newBookingsIds,
    conflictErrors,
    translatableErrors,
    errors,
    totalCount,
    current,
    error: resolveBulkBookingError(errors?.at(-1)),
  } satisfies Partial<IBookingsPollingUiState>;

  const percent =
    totalCount && current
      ? Math.min(Number(((current + 1) / totalCount) * 100), 100)
      : 0;

  const safePercent = isNaN(percent) ? 0 : percent;

  switch (status) {
    case BookingsPollingStatusEnum.Initializing:
      return {
        ...baseState,
        isLoading: true,
        isAborted,
        loadPercentage: 0,
        error: null,
        conflictError: null,
        newBookingsIds: [],
      };

    case BookingsPollingStatusEnum.InProgress:
      return {
        ...baseState,
        isLoading: true,
        isAborted,
        loadPercentage: safePercent,
      };

    case BookingsPollingStatusEnum.Completed:
      Log.debug("Polling state:  COMPLETED", pollingState);
      return {
        ...baseState,
        isLoading: false,
        isAborted,
        loadPercentage: FULL_LOAD_BULK_SET_BOOKINGS_PERCENT,
        isFinished: true,
      };

    case BookingsPollingStatusEnum.Aborted:
      Log.debug("Polling state:  ABORTED", pollingState);
      return {
        ...baseState,
        isLoading: false,
        isAborted: true,
        loadPercentage: 0,
        newBookingsIds: [],
      };

    case BookingsPollingStatusEnum.Error:
      Log.debug("Polling state:  ERROR", pollingState);
      return {
        ...baseState,
        isLoading: false,
        isAborted,
        loadPercentage: 0,
        newBookingsIds: [],
      };

    case BookingsPollingStatusEnum.ConflictError:
      Log.debug("Polling state: CONFLICT ERROR", pollingState);
      return {
        ...baseState,
        isLoading: true,
        loadPercentage: safePercent,
      };

    default:
      return defaultBookingBulkCreateState;
  }
};
